export interface FinancialData {
  month: string;
  date: Date;
  totalIncome: number;
  totalCOGS: number;
  grossProfit: number;
  totalExpense: number;
  netOrdinaryIncome: number;
  quarter: string;
  season: 'Winter' | 'Spring' | 'Summer' | 'Fall';
  year: number;
  sde?: number;
  sdeBreakdown?: {
    'Business Personal': number;
    'Farm Account': number;
    'Meals': number;
    'Net Income': number;
    'Officer Life': number;
    'Owner Salary': number;
    'Personal Draw': number;
    'Personal Property': number;
    'Simple IRA': number;
    'Travel & Entertainment': number;
    'Vehicle Expense': number;
    'SDE': number;
  };
}

// Helper function to parse currency strings
const parseCurrency = (value: string): number => {
  return parseFloat(value.replace(/[$,]/g, ''));
};

// Helper function to determine season based on month
const getSeason = (month: number): 'Winter' | 'Spring' | 'Summer' | 'Fall' => {
  if (month >= 12 || month <= 2) return 'Winter';
  if (month >= 3 && month <= 5) return 'Spring';
  if (month >= 6 && month <= 8) return 'Summer';
  return 'Fall';
};

// Helper function to get quarter
const getQuarter = (month: number): string => {
  if (month <= 3) return 'Q1';
  if (month <= 6) return 'Q2';
  if (month <= 9) return 'Q3';
  return 'Q4';
};

// Raw data from CSV
const rawData = [
  { month: 'Jan 20', totalIncome: '$38,700.73', totalCOGS: '$38,809.25', grossProfit: '-$108.52', totalExpense: '$24,457.45', netOrdinaryIncome: '-$24,565.97' },
  { month: 'Feb 20', totalIncome: '$63,090.55', totalCOGS: '$33,165.64', grossProfit: '$29,924.91', totalExpense: '$28,949.04', netOrdinaryIncome: '$975.87' },
  { month: 'Mar 20', totalIncome: '$61,460.99', totalCOGS: '$45,771.71', grossProfit: '$15,689.28', totalExpense: '$25,138.14', netOrdinaryIncome: '-$9,448.86' },
  { month: 'Apr 20', totalIncome: '$134,315.02', totalCOGS: '$40,549.80', grossProfit: '$93,765.22', totalExpense: '$21,982.74', netOrdinaryIncome: '$71,782.48' },
  { month: 'May 20', totalIncome: '$80,842.38', totalCOGS: '$40,053.27', grossProfit: '$40,789.11', totalExpense: '$28,070.97', netOrdinaryIncome: '$12,718.14' },
  { month: 'Jun 20', totalIncome: '$126,362.16', totalCOGS: '$57,878.15', grossProfit: '$68,484.01', totalExpense: '$33,798.89', netOrdinaryIncome: '$34,685.12' },
  { month: 'Jul 20', totalIncome: '$119,203.28', totalCOGS: '$62,151.41', grossProfit: '$57,051.87', totalExpense: '$23,744.51', netOrdinaryIncome: '$33,307.36' },
  { month: 'Aug 20', totalIncome: '$92,093.15', totalCOGS: '$42,971.23', grossProfit: '$49,121.92', totalExpense: '$20,146.96', netOrdinaryIncome: '$28,974.96' },
  { month: 'Sep 20', totalIncome: '$89,211.66', totalCOGS: '$31,987.15', grossProfit: '$57,224.51', totalExpense: '$21,326.18', netOrdinaryIncome: '$35,898.33' },
  { month: 'Oct 20', totalIncome: '$74,913.65', totalCOGS: '$51,779.50', grossProfit: '$23,134.15', totalExpense: '$26,789.93', netOrdinaryIncome: '-$3,655.78' },
  { month: 'Nov 20', totalIncome: '$51,277.80', totalCOGS: '$46,354.32', grossProfit: '$4,923.48', totalExpense: '$20,558.70', netOrdinaryIncome: '-$15,635.22' },
  { month: 'Dec 20', totalIncome: '$64,986.73', totalCOGS: '$25,074.14', grossProfit: '$39,912.59', totalExpense: '$25,855.82', netOrdinaryIncome: '$14,056.77' },
  { month: 'Jan 21', totalIncome: '$53,376.44', totalCOGS: '$26,290.98', grossProfit: '$27,085.46', totalExpense: '$22,082.48', netOrdinaryIncome: '$5,002.98' },
  { month: 'Feb 21', totalIncome: '$79,369.59', totalCOGS: '$22,551.80', grossProfit: '$56,817.79', totalExpense: '$21,131.41', netOrdinaryIncome: '$35,686.38' },
  { month: 'Mar 21', totalIncome: '$31,398.87', totalCOGS: '$23,507.35', grossProfit: '$7,891.52', totalExpense: '$29,581.42', netOrdinaryIncome: '-$21,689.90' },
  { month: 'Apr 21', totalIncome: '$100,960.90', totalCOGS: '$43,543.74', grossProfit: '$57,417.16', totalExpense: '$47,528.49', netOrdinaryIncome: '$9,888.67' },
  { month: 'May 21', totalIncome: '$66,340.10', totalCOGS: '$43,126.02', grossProfit: '$23,214.08', totalExpense: '$25,342.32', netOrdinaryIncome: '-$2,128.24' },
  { month: 'Jun 21', totalIncome: '$114,600.89', totalCOGS: '$52,340.40', grossProfit: '$62,260.49', totalExpense: '$21,438.13', netOrdinaryIncome: '$40,822.36' },
  { month: 'Jul 21', totalIncome: '$100,117.19', totalCOGS: '$38,998.95', grossProfit: '$61,118.24', totalExpense: '$24,710.07', netOrdinaryIncome: '$36,408.17' },
  { month: 'Aug 21', totalIncome: '$133,285.30', totalCOGS: '$65,803.77', grossProfit: '$67,481.53', totalExpense: '$28,134.59', netOrdinaryIncome: '$39,346.94' },
  { month: 'Sep 21', totalIncome: '$82,979.48', totalCOGS: '$57,270.49', grossProfit: '$25,708.99', totalExpense: '$21,018.84', netOrdinaryIncome: '$4,690.15' },
  { month: 'Oct 21', totalIncome: '$73,402.49', totalCOGS: '$57,915.52', grossProfit: '$15,486.97', totalExpense: '$25,557.94', netOrdinaryIncome: '-$10,070.97' },
  { month: 'Nov 21', totalIncome: '$61,596.35', totalCOGS: '$45,198.29', grossProfit: '$16,398.06', totalExpense: '$21,219.27', netOrdinaryIncome: '-$4,821.21' },
  { month: 'Dec 21', totalIncome: '$72,418.78', totalCOGS: '$42,757.35', grossProfit: '$29,661.43', totalExpense: '$24,093.75', netOrdinaryIncome: '$5,567.68' },
  { month: 'Jan 22', totalIncome: '$90,522.02', totalCOGS: '$35,259.52', grossProfit: '$55,262.50', totalExpense: '$34,055.40', netOrdinaryIncome: '$21,207.10' },
  { month: 'Feb 22', totalIncome: '$112,631.98', totalCOGS: '$46,836.47', grossProfit: '$65,795.51', totalExpense: '$23,752.00', netOrdinaryIncome: '$42,043.51' },
  { month: 'Mar 22', totalIncome: '$120,222.83', totalCOGS: '$71,673.03', grossProfit: '$48,549.80', totalExpense: '$21,401.63', netOrdinaryIncome: '$27,148.17' },
  { month: 'Apr 22', totalIncome: '$138,598.21', totalCOGS: '$70,532.73', grossProfit: '$68,065.48', totalExpense: '$31,651.80', netOrdinaryIncome: '$36,413.68' },
  { month: 'May 22', totalIncome: '$99,678.50', totalCOGS: '$81,852.30', grossProfit: '$17,826.20', totalExpense: '$24,489.38', netOrdinaryIncome: '-$6,663.18' },
  { month: 'Jun 22', totalIncome: '$227,970.23', totalCOGS: '$81,900.03', grossProfit: '$146,070.20', totalExpense: '$36,214.87', netOrdinaryIncome: '$109,855.33' },
  { month: 'Jul 22', totalIncome: '$145,172.15', totalCOGS: '$51,763.40', grossProfit: '$93,408.75', totalExpense: '$31,784.44', netOrdinaryIncome: '$61,624.31' },
  { month: 'Aug 22', totalIncome: '$56,170.21', totalCOGS: '$84,444.90', grossProfit: '-$28,274.69', totalExpense: '$23,254.90', netOrdinaryIncome: '-$51,529.59' },
  { month: 'Sep 22', totalIncome: '$180,902.34', totalCOGS: '$58,361.77', grossProfit: '$122,540.57', totalExpense: '$36,238.32', netOrdinaryIncome: '$86,302.25' },
  { month: 'Oct 22', totalIncome: '$76,781.37', totalCOGS: '$54,142.69', grossProfit: '$22,638.68', totalExpense: '$31,091.35', netOrdinaryIncome: '-$8,452.67' },
  { month: 'Nov 22', totalIncome: '$85,764.57', totalCOGS: '$50,398.96', grossProfit: '$35,365.61', totalExpense: '$25,902.37', netOrdinaryIncome: '$9,463.24' },
  { month: 'Dec 22', totalIncome: '$195,332.86', totalCOGS: '$91,884.10', grossProfit: '$103,448.76', totalExpense: '$33,006.70', netOrdinaryIncome: '$70,442.06' },
  { month: 'Jan 23', totalIncome: '$108,093.64', totalCOGS: '$82,433.61', grossProfit: '$25,660.03', totalExpense: '$23,082.08', netOrdinaryIncome: '$2,577.95' },
  { month: 'Feb 23', totalIncome: '$85,400.60', totalCOGS: '$60,929.61', grossProfit: '$24,470.99', totalExpense: '$24,733.03', netOrdinaryIncome: '-$262.04' },
  { month: 'Mar 23', totalIncome: '$74,139.49', totalCOGS: '$50,280.20', grossProfit: '$23,859.29', totalExpense: '$28,902.10', netOrdinaryIncome: '-$5,042.81' },
  { month: 'Apr 23', totalIncome: '$135,327.21', totalCOGS: '$45,311.25', grossProfit: '$90,015.96', totalExpense: '$28,055.37', netOrdinaryIncome: '$61,960.59' },
  { month: 'May 23', totalIncome: '$104,874.88', totalCOGS: '$68,324.56', grossProfit: '$36,550.32', totalExpense: '$25,860.86', netOrdinaryIncome: '$10,689.46' },
  { month: 'Jun 23', totalIncome: '$138,474.96', totalCOGS: '$31,361.15', grossProfit: '$107,113.81', totalExpense: '$46,249.19', netOrdinaryIncome: '$60,864.62' },
  { month: 'Jul 23', totalIncome: '$172,255.03', totalCOGS: '$63,814.60', grossProfit: '$108,440.43', totalExpense: '$51,925.46', netOrdinaryIncome: '$56,514.97' },
  { month: 'Aug 23', totalIncome: '$166,648.84', totalCOGS: '$114,697.70', grossProfit: '$51,951.14', totalExpense: '$31,031.01', netOrdinaryIncome: '$20,920.13' },
  { month: 'Sep 23', totalIncome: '$204,028.57', totalCOGS: '$92,637.18', grossProfit: '$111,391.39', totalExpense: '$82,691.05', netOrdinaryIncome: '$28,700.34' },
  { month: 'Oct 23', totalIncome: '$123,540.11', totalCOGS: '$91,190.25', grossProfit: '$32,349.86', totalExpense: '$23,069.04', netOrdinaryIncome: '$9,280.82' },
  { month: 'Nov 23', totalIncome: '$129,861.24', totalCOGS: '$56,814.64', grossProfit: '$73,046.60', totalExpense: '$25,431.49', netOrdinaryIncome: '$47,615.11' },
  { month: 'Dec 23', totalIncome: '$140,801.48', totalCOGS: '$73,063.36', grossProfit: '$67,738.12', totalExpense: '$33,945.22', netOrdinaryIncome: '$33,792.90' },
  { month: 'Jan 24', totalIncome: '$116,966.14', totalCOGS: '$42,380.03', grossProfit: '$74,586.11', totalExpense: '$21,445.86', netOrdinaryIncome: '$53,140.25' },
  { month: 'Feb 24', totalIncome: '$76,775.51', totalCOGS: '$60,950.18', grossProfit: '$15,825.33', totalExpense: '$28,208.01', netOrdinaryIncome: '-$12,382.68' },
  { month: 'Mar 24', totalIncome: '$76,841.82', totalCOGS: '$34,774.62', grossProfit: '$42,067.20', totalExpense: '$37,030.14', netOrdinaryIncome: '$5,037.06' },
  { month: 'Apr 24', totalIncome: '$72,569.64', totalCOGS: '$37,660.58', grossProfit: '$34,909.06', totalExpense: '$28,752.53', netOrdinaryIncome: '$6,156.53' },
  { month: 'May 24', totalIncome: '$132,031.99', totalCOGS: '$48,145.56', grossProfit: '$83,886.43', totalExpense: '$35,632.26', netOrdinaryIncome: '$48,254.17' },
  { month: 'Jun 24', totalIncome: '$93,125.58', totalCOGS: '$69,152.97', grossProfit: '$23,972.61', totalExpense: '$46,253.97', netOrdinaryIncome: '-$22,281.36' },
  { month: 'Jul 24', totalIncome: '$128,446.35', totalCOGS: '$69,677.91', grossProfit: '$58,768.44', totalExpense: '$28,843.13', netOrdinaryIncome: '$29,925.31' },
  { month: 'Aug 24', totalIncome: '$216,146.58', totalCOGS: '$79,103.10', grossProfit: '$137,043.48', totalExpense: '$38,185.23', netOrdinaryIncome: '$98,858.25' },
  { month: 'Sep 24', totalIncome: '$69,824.24', totalCOGS: '$64,530.08', grossProfit: '$5,294.16', totalExpense: '$30,891.20', netOrdinaryIncome: '-$25,597.04' },
  { month: 'Oct 24', totalIncome: '$98,214.37', totalCOGS: '$40,422.38', grossProfit: '$57,791.99', totalExpense: '$24,497.50', netOrdinaryIncome: '$33,294.49' },
  { month: 'Nov 24', totalIncome: '$40,773.88', totalCOGS: '$22,632.95', grossProfit: '$18,140.93', totalExpense: '$26,650.03', netOrdinaryIncome: '-$8,509.10' },
  { month: 'Dec 24', totalIncome: '$117,689.92', totalCOGS: '$59,287.49', grossProfit: '$58,402.43', totalExpense: '$70,279.46', netOrdinaryIncome: '-$11,877.03' },
  { month: 'Jan 25', totalIncome: '$130,598.85', totalCOGS: '$50,737.42', grossProfit: '$79,861.43', totalExpense: '$36,393.35', netOrdinaryIncome: '$43,468.08' },
  { month: 'Feb 25', totalIncome: '$107,611.00', totalCOGS: '$84,663.88', grossProfit: '$22,947.12', totalExpense: '$23,146.86', netOrdinaryIncome: '-$199.74' },
  { month: 'Mar 25', totalIncome: '$54,134.00', totalCOGS: '$44,438.23', grossProfit: '$9,695.77', totalExpense: '$28,900.50', netOrdinaryIncome: '-$19,204.73' },
  { month: 'Apr 25', totalIncome: '$134,199.45', totalCOGS: '$67,232.05', grossProfit: '$66,967.40', totalExpense: '$29,165.68', netOrdinaryIncome: '$37,801.72' },
  { month: 'May 25', totalIncome: '$125,654.00', totalCOGS: '$33,447.34', grossProfit: '$92,206.66', totalExpense: '$31,454.17', netOrdinaryIncome: '$60,752.49' }
];

// Process the data
export const financialData: FinancialData[] = rawData.map((item, index) => {
  const [monthAbbr, year] = item.month.split(' ');
  const monthMap: { [key: string]: number } = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
  };
  
  const monthNum = monthMap[monthAbbr];
  const fullYear = parseInt(`20${year}`);
  const date = new Date(fullYear, monthNum - 1, 1);
  
  return {
    month: item.month,
    date,
    totalIncome: parseCurrency(item.totalIncome),
    totalCOGS: parseCurrency(item.totalCOGS),
    grossProfit: parseCurrency(item.grossProfit),
    totalExpense: parseCurrency(item.totalExpense),
    netOrdinaryIncome: parseCurrency(item.netOrdinaryIncome),
    quarter: `${fullYear} ${getQuarter(monthNum)}`,
    season: getSeason(monthNum),
    year: fullYear
  };
});

// Aggregate data by season
export const seasonalData = financialData.reduce((acc, item) => {
  const existing = acc.find(s => s.season === item.season);
  if (existing) {
    existing.totalIncome += item.totalIncome;
    existing.totalCOGS += item.totalCOGS;
    existing.grossProfit += item.grossProfit;
    existing.count += 1;
  } else {
    acc.push({
      season: item.season,
      totalIncome: item.totalIncome,
      totalCOGS: item.totalCOGS,
      grossProfit: item.grossProfit,
      count: 1
    });
  }
  return acc;
}, [] as Array<{ season: string; totalIncome: number; totalCOGS: number; grossProfit: number; count: number }>);

// Calculate averages for seasonal data
export const seasonalAverages = seasonalData.map(item => ({
  season: item.season,
  avgIncome: item.totalIncome / item.count,
  avgCOGS: item.totalCOGS / item.count,
  avgGrossProfit: item.grossProfit / item.count
}));

// Aggregate data by quarter
export const quarterlyData = financialData.reduce((acc, item) => {
  const existing = acc.find(q => q.quarter === item.quarter);
  if (existing) {
    existing.totalIncome += item.totalIncome;
    existing.totalCOGS += item.totalCOGS;
    existing.grossProfit += item.grossProfit;
    existing.totalExpense += item.totalExpense;
    existing.netOrdinaryIncome += item.netOrdinaryIncome;
  } else {
    acc.push({
      quarter: item.quarter,
      totalIncome: item.totalIncome,
      totalCOGS: item.totalCOGS,
      grossProfit: item.grossProfit,
      totalExpense: item.totalExpense,
      netOrdinaryIncome: item.netOrdinaryIncome
    });
  }
  return acc;
}, [] as Array<{ quarter: string; totalIncome: number; totalCOGS: number; grossProfit: number; totalExpense: number; netOrdinaryIncome: number }>);

// Aggregate data by year
export const yearlyData = financialData.reduce((acc, item) => {
  const existing = acc.find(y => y.year === item.year);
  if (existing) {
    existing.totalIncome += item.totalIncome;
    existing.totalCOGS += item.totalCOGS;
    existing.grossProfit += item.grossProfit;
    existing.totalExpense += item.totalExpense;
    existing.netOrdinaryIncome += item.netOrdinaryIncome;
    existing.monthCount += 1;
  } else {
    acc.push({
      year: item.year,
      month: `${item.year}`, // Use year as month for display consistency
      date: new Date(item.year, 0, 1), // January 1st of the year
      totalIncome: item.totalIncome,
      totalCOGS: item.totalCOGS,
      grossProfit: item.grossProfit,
      totalExpense: item.totalExpense,
      netOrdinaryIncome: item.netOrdinaryIncome,
      quarter: `${item.year}`, // Use year as quarter for consistency
      season: 'Winter' as const, // Default season, not really applicable for yearly data
      monthCount: 1
    });
  }
  return acc;
}, [] as Array<FinancialData & { monthCount: number }>);

// Utility function to get data based on view mode
export const getDataByViewMode = (viewMode: 'monthly' | 'yearly', data?: FinancialData[]): FinancialData[] => {
  const sourceData = data || financialData;
  
  if (viewMode === 'yearly') {
    // Group by year and calculate averages
    const yearlyGroups = sourceData.reduce((acc, item) => {
      if (!acc[item.year]) {
        acc[item.year] = [];
      }
      acc[item.year].push(item);
      return acc;
    }, {} as Record<number, FinancialData[]>);

    return Object.entries(yearlyGroups).map(([year, yearData]) => {
      const totalIncome = yearData.reduce((sum, item) => sum + item.totalIncome, 0);
      const totalCOGS = yearData.reduce((sum, item) => sum + item.totalCOGS, 0);
      const totalExpense = yearData.reduce((sum, item) => sum + item.totalExpense, 0);
      const monthCount = yearData.length;

      return {
        month: year,
        date: new Date(parseInt(year), 0, 1),
        totalIncome: totalIncome / monthCount,
        totalCOGS: totalCOGS / monthCount,
        grossProfit: (totalIncome - totalCOGS) / monthCount,
        totalExpense: totalExpense / monthCount,
        netOrdinaryIncome: (totalIncome - totalCOGS - totalExpense) / monthCount,
        quarter: `${year}`,
        season: 'Winter' as const,
        year: parseInt(year),
        sde: yearData.reduce((sum, item) => sum + (item.sde || 0), 0) / monthCount,
      };
    }).sort((a, b) => a.year - b.year);
  }
  
  return sourceData;
};